import React, { useState, useEffect, useRef } from "react";
import { Chessboard } from "react-chessboard";
import { Chess } from "chess.js";
import styles from "./index.module.css";
import { Sparkels } from "../../Components";
import { initial_openings, main_server } from "../../constants";

const OpeningSquare = ({ opening, onClick, isLarge = false, windowSize }) => {
  const game = new Chess();
  game.load(opening.fen);
  
  const boardWrapperRef = useRef(null);
  const [boardWidth, setBoardWidth] = useState(isLarge ? 600 : 200);
  
  useEffect(() => {
    const updateBoardWidth = () => {
      if (boardWrapperRef.current) {
        const containerWidth = boardWrapperRef.current.clientWidth;
        // For large squares, limit to 40% of window width and 50% of window height
        
          // For regular squares, use 100% of container width
          setBoardWidth(containerWidth);
        
      }
    };
    
    updateBoardWidth();
    
    // Add resize event listener
    window.addEventListener('resize', updateBoardWidth);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', updateBoardWidth);
    };
  }, [isLarge, windowSize]);
  
  const totalGames = opening.totalGames;
  const winPercentage = (opening.totalWins / totalGames) * 100;
  const drawPercentage = (opening.totalDraws / totalGames) * 100;
  const lossPercentage = (opening.totalLosses / totalGames) * 100;

  return (
    <div 
      className={`${styles.openingSquare} ${isLarge ? styles.largeSquare : ''}`} 
      onClick={() => !isLarge && onClick(opening)}
    >
      {/* Play rate indicator */}
      <div className={styles.playRateContainer}>
        <div 
          className={styles.playRateBar} 
          style={{ width: `${opening.playRate}%` }}
        ></div>
        <div className={styles.playRateSquare}>
          <span>{opening.playRate}%</span>
        </div>
      </div>
      
      {/* Opening name */}
      <div className={styles.openingName}>
        <h3>{opening.name}</h3>
        <p>{opening.move}</p>
      </div>
      
      {/* Chessboard */}
      <div className={styles.boardContainer}>
        <div className={styles.boardWrapper} ref={boardWrapperRef}>
          <Chessboard 
            id={`board-${opening.name.replace(/\s+/g, '-').toLowerCase()}`}
            position={opening.fen}
            boardWidth={boardWidth}
            customBoardStyle={{ width: '100%', maxWidth: '100%' }}
            arePiecesDraggable={false}
          />
        </div>
        
        {/* Win/Draw/Loss bar */}
        <div className={styles.statsBar}>
          <div 
            className={styles.winBar} 
            style={{ width: `${winPercentage}%` }}
          >
            <span>{Math.round(winPercentage)}%</span>
          </div>
          <div 
            className={styles.drawBar} 
            style={{ width: `${drawPercentage}%` }}
          >
            <span>{Math.round(drawPercentage)}%</span>
          </div>
          <div 
            className={styles.lossBar} 
            style={{ width: `${lossPercentage}%` }}
          >
            <span>{Math.round(lossPercentage)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Openings = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOpening, setSelectedOpening] = useState(null);
  const [filteredOpenings, setFilteredOpenings] = useState([]);
  const [moves, setMoves] = useState(initial_openings);
  const total_first_moves = 5381312;
  const [move_index, setMoveIndex] = useState(1);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  useEffect(() => {
    const transformed = moves.map((val) => {
      var totalGames = 0;
      const totalWins = (val.stats[0] ?? {stat: [0]}).stat[0];
      const totalDraws = (val.stats[0] ?? {stat: [0, 0]}).stat[1];
      const totalLosses = (val.stats[0] ?? {stat: [0, 0, 0]}).stat[2];
      totalGames += totalWins;
      totalGames += totalDraws;
      totalGames += totalLosses;
      return {name: val.name, move: val.moves.join(' '), fen: val.fen, playRate: ((totalGames / total_first_moves) * 100).toFixed(2), totalGames, totalDraws, totalWins, totalLosses}
    }).reverse();
      setFilteredOpenings(transformed);
  }, [moves]);

  useEffect(() => {
    if (typeof searchTerm === 'string' && searchTerm.length > 0) {
      if (searchTerm[searchTerm.length - 1] !== ' ') getNextOpening(selectedOpening, searchTerm);
  } else {
    setMoves(initial_openings);
  }
  }, [searchTerm]);

  async function getNextOpening(opening, pos = null) {
    const response = await fetch(
            main_server + `opening_explore?pos=${pos === null ? selectedOpening.move.split(' ').join(',') : (pos.includes(' ') ? pos.split(' ').join(',') : pos)}&index=${move_index}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "text/plain",
                "Authorization": `Bearer ${localStorage.getItem('chesscloud-token')}`
              },
            },
          );
          if (response.status === 200) {
            setMoveIndex(move_index + 1);
            try {
              const data = await response.json();
              if (Array.isArray(data)) {
                setMoves(data);
              }
            } catch (e) {
              console.log(e);
            }
          }
  }

  useEffect(() => {
    if (selectedOpening !== null && selectedOpening !== undefined) {
      getNextOpening(selectedOpening);
    }
  }, [selectedOpening]);
  
  const handleOpeningClick = (opening) => {
    console.log(opening);
    setSelectedOpening(opening);
  }

  return (
    <div className={styles.container}>
      <div className={styles.heroSection}>
        <h1>Chess Openings Explorer</h1>
        <Sparkels />
        <p>
          Explore the most popular chess openings, their statistics, and learn how to play them effectively.
          Our database includes millions of master games to provide accurate statistics and insights.
        </p>
      </div>
      
      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search openings by  moves or name (min 4 characters)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>
      
      {selectedOpening && (
        <div className={styles.selectedOpeningContainer}>
          <div className={styles.selectedOpeningInfo}>
            <OpeningSquare opening={selectedOpening} onClick={() => {}} isLarge={true} windowSize={windowSize} />
          </div>
          <div className={styles.openingActions}>
            <button onClick={() => window.open(`https://analysis.chessmind.io?fen=${selectedOpening.fen}&explore=true`)} className={styles.actionButton}>
              Explore Position
            </button>
            <button onClick={() => window.open(`https://analysis.chessmind.io/puzzles?opening=${selectedOpening.name.replace(`'`, '').split(' ').join('_')}`)} className={styles.actionButton}>
              Play Puzzles from this Opening
            </button>
            <button onClick={() => window.open(`https://analysis.chessmind.io/play?mode=computer&fen=${encodeURIComponent(selectedOpening.fen)}`)} className={styles.actionButton}>
              Play Against Bot
            </button>
            <button 
              className={`${styles.actionButton} ${styles.closeButton}`}
              onClick={() => setSelectedOpening(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      <div key={move_index} className={styles.openingsGrid}>
        {filteredOpenings.map((opening, index) => (
          <OpeningSquare 
            key={index} 
            opening={opening} 
            onClick={handleOpeningClick}
            windowSize={windowSize}
          />
        ))}
      </div>
    </div>
  );
};

export default Openings;
